import React, { useState } from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";
import { useI18n } from "Hooks";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { ChevronDownOutlined } from "@toddle-edu/ds-icons";
import AnimateHeight from "UIComponents/AnimateHeight";
import { onEnterOrSpaceKeyDown } from "Utils";
import classes from "./EventParticipantList.scss";

import { getOrganizationId } from "modules/CommonSelectors";

import ParticipantsSummary from "MyCalendar/components/ParticipantsSummary";
import ParticipantsPreview from "MyCalendar/components/EventModalV2/components/ParticipantsPreview";
import {
  TYPENAME_MAPPING,
  formatClassesStructure,
} from "MyCalendar/components/EventModalV2/modules/Utils";
import { isNewParticipantSelectorEnabled } from "MyCalendar/modules/Utils";
import { getUserCountByEntityandEntityIds } from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";
import {
  PARTICIPANT_USER_TYPES,
  USER_TYPE_COUNT_FIELDS,
  ENTITY_TYPE,
} from "MyCalendar/modules/MyCalendarModule/MyCalendarConstants";
import { getSelectedAcademicYearIdForSchool } from "MyCalendar/modules/Utils";
import _ from "lodash";

const getSubtextString = ({ t, userCountByEntity, sharedWith }) => {
  return _.reduce(
    sharedWith,
    (result, userType, index) => {
      const userTypeCount = _.get(
        userCountByEntity,
        USER_TYPE_COUNT_FIELDS[userType],
        0
      );
      return _.concat(
        result,
        `${index ? ", " : ""}${userTypeCount} ${t(
          PARTICIPANT_USER_TYPES[userType],
          {
            count: userTypeCount,
          }
        )}`
      );
    },
    ""
  );
};

const EventParticipantList = props => {
  const {
    sharedWith,
    userCountByEntity,
    isUserCountLoading,
    eventTitle,
    associatedEntities,
    isEventDetailsLoading,
    isStaff,
    userByUserType,
    participantListType,
    isAssociatedEntitiesDetailsLoading,
    testId,
    eventType,
  } = props;
  const { t } = useI18n();

  const classesAsAssociatedEntities = formatClassesStructure(
    _.map(associatedEntities, entity => {
      return { node: entity };
    })
  );

  const [isExpandedViewVisible, setisExpandedViewVisible] = useState(false);

  const arrowIconStyle = classNames(
    { [classes.arrowIcon]: true },
    { [classes.arrowIconReversed]: isExpandedViewVisible }
  );

  const showStudentHandler = () => {
    if (isStaff) setisExpandedViewVisible(state => !state);
  };

  const handleKeyDown = event => {
    onEnterOrSpaceKeyDown(event, showStudentHandler);
  };

  const staffCount = _.includes(sharedWith, "STAFF")
    ? userCountByEntity?.staffCount
    : 0;
  const studentCount = _.includes(sharedWith, "STUDENT")
    ? userCountByEntity?.studentCount
    : 0;
  const familyCount = _.includes(sharedWith, "PARENT")
    ? userCountByEntity?.familyCount
    : 0;
  const totalParticipants = staffCount + studentCount + familyCount;

  if (isNewParticipantSelectorEnabled()) {
    return (
      <div className={classes.courseDetailsV2}>
        <div className={classes.courseDetailsBody}>
          {isUserCountLoading ||
          (isEventDetailsLoading && _.isEmpty(associatedEntities)) ? (
            <>
              <div className={classes.assignedToLabel}>
                <SkeletonLoader width="50%" height="18px" />
              </div>
              <div className={classNames("text-body", classes.courseSummary)}>
                <SkeletonLoader width="100%" height="18px" />
              </div>
            </>
          ) : (
            <ParticipantsSummary
              totalCount={totalParticipants}
              summaryText={getSubtextString({
                t,
                userCountByEntity,
                sharedWith,
              })}
              eventTitle={eventTitle}
              associatedEntities={associatedEntities}
              sharedWith={sharedWith}
              staffCount={staffCount}
              studentCount={studentCount}
              familyCount={familyCount}
              testId={testId}
              eventType={eventType}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.courseDetails}>
      <div
        className={classes.courseDetailsHeader}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={showStudentHandler}
        data-testId={"event-details-participant-list"}
      >
        <div className={classes.left}>
          <div className={classes.assignedToLabel}>
            {isUserCountLoading ||
            (isEventDetailsLoading && _.isEmpty(associatedEntities)) ? (
              <SkeletonLoader width="50%" height="18px" />
            ) : (
              `${t("common:participants")} (${totalParticipants})`
            )}
          </div>
          <div className={classNames("text-body", classes.courseSummary)}>
            {isUserCountLoading ||
            (isEventDetailsLoading && _.isEmpty(associatedEntities)) ? (
              <SkeletonLoader width="100%" height="18px" />
            ) : (
              getSubtextString({ t, userCountByEntity, sharedWith })
            )}
          </div>
        </div>
        {isStaff ? (
          <div className={classes.right}>
            <div className={arrowIconStyle}>
              <ChevronDownOutlined />
            </div>
          </div>
        ) : null}
      </div>
      <AnimateHeight
        height={isExpandedViewVisible ? "auto" : 0}
        duration={300}
        onAnimationStart={null}
        onAnimationEnd={null}
        animateOpacity={true}
      >
        <div className={classes.courseDetailsBody}>
          <ParticipantsPreview
            grades={associatedEntities}
            courses={classesAsAssociatedEntities}
            participantListTypeProp={participantListType}
            sharedWithProp={sharedWith}
            isQuickViewModal={true}
            userCountByEntityProp={userCountByEntity}
            userByUserTypeProp={userByUserType}
            isAssociatedEntitiesDetailsLoading={
              isAssociatedEntitiesDetailsLoading
            }
          />
        </div>
      </AnimateHeight>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = getOrganizationId(state);
  const academicYearId = getSelectedAcademicYearIdForSchool(state);

  const {
    associatedEntities,
    users,
    isAssociatedEntitiesDetailsLoading,
    isEventDetailsLoading,
  } = ownProps;

  // associatedEntities will be empty in case of custom participant list, users will be used instead
  const typeName = _.get(associatedEntities, "0.__typename", "");
  const participantListType = TYPENAME_MAPPING[typeName] || "CUSTOM";

  // Format participants for CUSTOM entity type
  const formattedCustomParticipants = _.groupBy(users, "__typename");
  const userByUserType = {
    [ENTITY_TYPE.STAFF]: formattedCustomParticipants["Staff"] || [],
    [ENTITY_TYPE.STUDENT]: formattedCustomParticipants["Student"] || [],
    [ENTITY_TYPE.PARENT]: formattedCustomParticipants["FamilyMember"] || [],
  };
  const userCountByEntity = {
    staffCount: _.size(_.get(userByUserType, ENTITY_TYPE.STAFF, [])),
    studentCount: _.size(_.get(userByUserType, ENTITY_TYPE.STUDENT, [])),
    familyCount: _.size(_.get(userByUserType, ENTITY_TYPE.PARENT, [])),
  };

  return {
    organizationId,
    academicYearId,
    entityIds: _.map(associatedEntities, "id"),
    participantListType,
    userByUserType,
    userCountByEntity,
    associatedEntities: _.isEqual(participantListType, "CUSTOM")
      ? users
      : associatedEntities,
    isAssociatedEntitiesDetailsLoading,
    isEventDetailsLoading,
  };
};

const mapActionCreators = {};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserCountByEntityandEntityIds, {
    name: "userCountByEntityCourseWithEntityIds",
    skip: ({ participantListType, entityIds }) =>
      !participantListType ||
      _.isEmpty(entityIds) ||
      participantListType === "CUSTOM",
    options: ({
      organizationId,
      participantListType,
      entityIds,
      academicYearId,
    }) => ({
      fetchPolicy: "cache-and-network",
      context: { useHttpLink: true },
      variables: {
        organizationId,
        input: {
          entity: participantListType,
          entityIds,
          academicYearId,
        },
      },
    }),
    props: ({ userCountByEntityCourseWithEntityIds }) => {
      const queryData = _.get(userCountByEntityCourseWithEntityIds, "node", {});
      const { networkStatus } = userCountByEntityCourseWithEntityIds;
      return {
        userCountByEntity: _.get(queryData, "users.userCountByEntity", {}),
        isUserCountLoading:
          _.includes([1, 2], networkStatus) && _.isEmpty(queryData),
      };
    },
  })
)(EventParticipantList);
